/**
 * /apps/:appId/tasks/:taskId — task detail (professional compact layout)
 *
 * Layout:
 *   • Header: breadcrumb left | Edit + Delete buttons right
 *   • Title (large) then task-number + status + priority pills
 *   • Tabs: Overview | Timeline | Quality Analysis
 *   • Overview: 2-column
 *       Left  (~65%): meta grid · timer · description · checklist
 *       Right (~35%): sticky sidebar — time logs + comments (icon buttons)
 *
 * Rules:
 *   • Only task creator may change deadline
 *   • Cannot mark done/cancelled with incomplete checklist (server enforced)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ConfirmDeleteModal from '../../components/workspace/ConfirmDeleteModal';
import CommentsPanel   from '../../components/workspace/task/CommentsPanel';
import TimeLogsPanel   from '../../components/workspace/task/TimeLogsPanel';
import ChecklistPanel  from '../../components/workspace/task/ChecklistPanel';
import TimelinePanel   from '../../components/workspace/task/TimelinePanel';
import QualityPanel    from '../../components/workspace/task/QualityPanel';
import CreateTaskModal  from '../../components/workspace/task/CreateTaskModal';
import TaskSourceTag    from '../../components/workspace/task/TaskSourceTag';
import { useApi }       from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { formatRelative, formatDate, formatDateTime, memberDisplayName } from '../../lib/format';
import { X, FileText } from 'lucide-react';
import { AttachmentDisplay, AttachmentModal } from '../../components/shared/AttachmentComponents';
import { renderMentionText } from '../../components/workspace/MentionTextarea';

const SERVICE_MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function linkifyText(text) {
  const parts = text.split(/(\bhttps?:\/\/[^\s]+)/g);
  return parts.map((part, i) =>
    /^\bhttps?:\/\//.test(part)
      ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', wordBreak: 'break-all' }}>{part}</a>
      : part
  );
}

function DescriptionWithAttachments({ description, attachments }) {
  const [modalAtt, setModalAtt] = React.useState(null);

  if (!attachments.length) {
    const cleaned = description.replace(/\n?\[attachment: [^\]]+\]\n?/g, '').replace(/^\n|\n$/g, '');
    if (!cleaned) return null;
    return (
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', lineHeight: 1.6, overflowWrap: 'anywhere', maxWidth: '100%', overflow: 'hidden' }}>
        {renderMentionText(cleaned)}
      </p>
    );
  }

  const attByName = {};
  const attById = {};
  for (const a of attachments) {
    if (a.file_name) attByName[a.file_name] = a;
    attById[String(a.id)] = a;
  }

  // Split on placeholders, interleave text and attachment previews
  const parts = description.split(/\n?\[attachment: ([^\]]+)\]\n?/);
  const renderedAttsInline = new Set();
  const nodes = parts.map((part, i) => {
    if (i % 2 === 0) {
      if (!part) return null;
      return (
        <span key={i} style={{ whiteSpace: 'pre-wrap', display: 'block', overflowWrap: 'anywhere', overflow: 'hidden' }}>{renderMentionText(part)}</span>
      );
    } else {
      // Support __id_X__ format (new) and legacy filename/id format
      const idMatch = part.match(/^__id_(\d+)__$/);
      const att = idMatch ? attById[idMatch[1]] : (attByName[part] || attById[part]);
      if (!att) return null;
      renderedAttsInline.add(att.id);
      const isImg = att.content_type?.startsWith('image/');
      const isVideo = att.content_type?.startsWith('video/');
      if (isImg) {
        return (
          <span key={i} style={{ display: 'block', margin: '6px 0' }}>
            <img
              src={att.file_url}
              alt={att.file_name || 'image'}
              style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 6, border: '1px solid var(--border)', cursor: 'pointer' }}
              onClick={() => setModalAtt(att)}
            />
          </span>
        );
      }
      if (isVideo) {
        return (
          <span key={i} style={{ display: 'inline-block', margin: '6px 0', position: 'relative', cursor: 'pointer', lineHeight: 0 }} onClick={() => setModalAtt(att)}>
            <video
              src={`${att.file_url}#t=0.1`}
              preload="metadata"
              muted
              playsInline
              style={{ maxWidth: '100%', maxHeight: 240, borderRadius: 6, border: '1px solid var(--border)', display: 'block', pointerEvents: 'none' }}
            />
            <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#fff', textShadow: '0 1px 6px rgba(0,0,0,0.7)', pointerEvents: 'none' }}>▶</span>
          </span>
        );
      }
      return (
        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, margin: '4px 0', padding: '3px 10px', borderRadius: 6, background: 'var(--bg-muted)', border: '1px solid var(--border)', fontSize: 13 }}>
          📎 <a href={att.file_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>{att.file_name || 'file'}</a>
        </span>
      );
    }
  });

  // Any attachments not referenced by a placeholder — show below
  const orphanAtts = attachments.filter(a => !renderedAttsInline.has(a.id));

  return (
    <>
      {modalAtt && <AttachmentModal attachment={modalAtt} onClose={() => setModalAtt(null)} />}
      <div style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.6 }}>
        {nodes}
        {orphanAtts.length > 0 && (
          <AttachmentDisplay attachments={orphanAtts} />
        )}
      </div>
    </>
  );
}

const STATUS_OPTIONS = [
  { value: 'open',        label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked',     label: 'Blocked' },
  { value: 'done',        label: 'Completed' },
  { value: 'cancelled',   label: 'Cancelled' },
];
const PRIORITY_OPTIONS = [
  { value: 'low',    label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high',   label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

function fmtMinutes(m) {
  if (!m) return '0 h';
  const h   = Math.floor(m / 60);
  const rem = m % 60;
  return h > 0 ? (rem > 0 ? `${h}h ${rem}m` : `${h}h`) : `${m}m`;
}

/** Format a number of hours cleanly: 5.0 → "5", 5.5 → "5.5" */
function fmtH(n) {
  const v = parseFloat(n) || 0;
  return v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1);
}

export default function TaskDetail() {
  const { appId, taskId } = useParams();
  const navigate          = useNavigate();
  const api               = useApi();
  const { me, canManage, canWrite } = useWorkspace();

  const [task, setTask]         = useState(null);
  const [members, setMembers]   = useState([]);
  const [roles, setRoles]       = useState([]);
  const [areasByRole, setAreas] = useState(new Map());
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [timerAlert, setTimerAlert] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const [editOpen, setEditOpen]         = useState(false);
  const [chkKey, setChkKey]             = useState(0);
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const sidebarRef = useRef(null);
  const [complianceTask, setComplianceTask] = useState(null);
  const [complianceSaving, setComplianceSaving] = useState(false);


  useEffect(() => {
    const el = sidebarRef.current;
    if (!el) return;
    let locked = false;
    const lockScroll = () => {
      if (locked) return;
      locked = true;
      const scrollY = window.scrollY;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.dataset.scrollY = String(scrollY);
    };
    const unlockScroll = () => {
      if (!locked) return;
      locked = false;
      const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };
    el.addEventListener('focusin', lockScroll);
    el.addEventListener('focusout', unlockScroll);
    return () => {
      el.removeEventListener('focusin', lockScroll);
      el.removeEventListener('focusout', unlockScroll);
      unlockScroll();
    };
  }, []);

  // Checklist progress cache
  const [chkProgress, setChkProgress] = useState(null);
  const [showChkWarning, setShowChkWarning] = useState(false);

  const handleProgressChange = useCallback((p) => {
    setChkProgress(p);
  }, []);

  // ── Live timer ──────────────────────────────────────────────────────────────
  const [timerSecs, setTimerSecs] = useState(0);
  const [timerLoading, setTimerLoading] = useState(false);
  const [timeLogReload, setTimeLogReload] = useState(0);
  const timerInterval             = useRef(null);
  const clientTimerStart          = useRef(null);

  useEffect(() => {
    if (!task) return;
    
    if (task.timer_started_at) {
      if (!clientTimerStart.current) {
        const startTimeStr = task.timer_started_at;
        const parsedTime = startTimeStr.endsWith('Z') ? startTimeStr : startTimeStr.replace(' ', 'T') + 'Z';
        clientTimerStart.current = new Date(parsedTime).getTime();
      }
    } else {
      clientTimerStart.current = null;
    }

    const tick = () => {
      if (clientTimerStart.current) {
        const elapsed = Math.floor((Date.now() - clientTimerStart.current) / 1000);
        setTimerSecs((task.timer_accumulated_s || 0) + elapsed);
      } else {
        setTimerSecs(task.timer_accumulated_s || 0);
      }
    };
    tick();
    if (task.timer_started_at) {
      clearInterval(timerInterval.current);
      timerInterval.current = setInterval(tick, 1000);
    } else {
      clearInterval(timerInterval.current);
    }
    return () => clearInterval(timerInterval.current);
  }, [task]);

  const fmtTimer = (s) => {
    const h   = Math.floor(s / 3600);
    const m   = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return [h, m, sec].map((n) => String(n).padStart(2, '0')).join(':');
  };

  const timerActionInProgress = useRef(false);

  const timerAction = async (action) => {
    if (timerLoading || timerActionInProgress.current) return;

    // If starting, check if another task's timer is already running
    if (action === 'start') {
      try {
        const { running } = await api.runningTimer(appId);
        if (running && running.id !== task.id) {
          const proceed = window.confirm(
            `"${running.title}" is already running.\n\nDo you want to stop & log it and start this task?`
          );
          if (!proceed) return;
          // Stop & log the other task first
          await api.taskTimer(appId, running.id, { action: 'stop' });
        }
      } catch (_) {}
    }

    if (action === 'stop' && timerSecs > 7200) {
      setTimerAlert(true);
      return;
    }

    timerActionInProgress.current = true;
    const originalTask = task;
    setError(null);
    setTimerLoading(true);

    // Optimistic UI updates
    if (action === 'start') {
      const nowMs = Date.now();
      clientTimerStart.current = nowMs;
      setTask(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          timer_started_at: new Date(nowMs).toISOString(),
          ...(prev.status === 'open' ? { status: 'in_progress' } : {}),
        };
      });
    } else if (action === 'pause') {
      const elapsed = clientTimerStart.current ? Math.floor((Date.now() - clientTimerStart.current) / 1000) : 0;
      clientTimerStart.current = null;
      setTask(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          timer_started_at: null,
          timer_accumulated_s: (prev.timer_accumulated_s || 0) + elapsed
        };
      });
    } else if (action === 'stop') {
      clientTimerStart.current = null;
      setTask(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          timer_started_at: null,
          timer_accumulated_s: 0
        };
      });
    }

    try {
      const updated = await api.taskTimer(appId, taskId, { action });
      setTask(updated);
      if (action === 'stop') {
        await loadTask();
        setTimeLogReload((n) => n + 1);
      }
    } catch (err) {
      setTask(originalTask);
      setError(err.response?.data?.error || err.message);
    } finally {
      timerActionInProgress.current = false;
      setTimerLoading(false);
    }
  };

  // ── Data loaders ────────────────────────────────────────────────────────────
  const loadTask = useCallback(async () => {
    setError(null);
    try {
      const t = await api.getTask(appId, taskId);
      if (t.priority === 'critical') t.priority = 'urgent';
      setTask(t);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  }, [api, appId, taskId]);


  const loadStatic = useCallback(async () => {
    const [m, r] = await Promise.all([
      api.listAppMembers(appId).catch(() => ({ items: [] })),
      api.roles.list(appId).catch(() => ({ items: [] })),
    ]);
    setMembers(m.items || []);
    setRoles(r.items || []);
    const perRole = await Promise.all(
      (r.items || []).map((role) =>
        api.listAreas(appId, role.id)
          .then((res) => ({ role, items: res.items || [] }))
          .catch(() => ({ role, items: [] }))
      )
    );
    const map = new Map();
    for (const { role, items } of perRole) map.set(role.id, items);
    setAreas(map);
  }, [api, appId]);

  useEffect(() => { loadTask(); },  [loadTask]);
  useEffect(() => { loadStatic(); }, [loadStatic]);

  useEffect(() => {
    if (!task?.contract_task_id) { setComplianceTask(null); return; }
    api.getContractTask(appId, task.contract_task_id)
      .then(r => setComplianceTask(r))
      .catch(() => setComplianceTask(null));
  }, [task?.contract_task_id]);

  // Dedicated handler for Start / Resume so they're never blocked by patch's lock
  const changeStatus = async (newStatus) => {
    if (statusChanging) return;
    setStatusChanging(true);
    setTask(prev => prev ? { ...prev, status: newStatus } : prev);
    try {
      const updated = await api.updateTask(appId, taskId, { status: newStatus });
      setTask(updated);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      await loadTask();
    } finally {
      setStatusChanging(false);
    }
  };

  const patchInProgress = useRef(false);

  const patch = async (body) => {
    if (patchInProgress.current) return;
    patchInProgress.current = true;
    const originalTask = task;
    setError(null);

    // Optimistically update local task state
    setTask(prev => {
      if (!prev) return prev;
      const next = { ...prev, ...body };
      if ('status' in body) {
        if (body.status === 'done' && prev.status !== 'done') {
          next.completed_at = new Date().toISOString();
        } else if (body.status !== 'done' && prev.status === 'done') {
          next.completed_at = null;
        }
      }
      return next;
    });

    try {
      const updated = await api.updateTask(appId, taskId, body);
      setTask(updated);
      if ('assigned_to_user_id' in body || 'status' in body) {
        setTimeLogReload((n) => n + 1);
      }
    } catch (err) {
      setTask(originalTask);
      setError(err.response?.data?.error || err.message);
    } finally {
      patchInProgress.current = false;
    }
  };

  if (loading && !task) return <p className="muted">Loading task…</p>;
  if (error   && !task) return <div className="banner banner-error">{error}</div>;
  if (!task)            return null;

  const memberName = (id) => {
    if (id == null) return '—';
    const u = members.find((m) => m.user_id === id);
    if (!u) return `User #${id}`;
    return memberDisplayName(u.first_name, u.last_name, u.email);
  };

  const isCreator      = task.created_by_user_id === me?.id;
  const isAssignee     = task.assigned_to_user_id === me?.id;
  const canEdit        = isCreator || isAssignee;
  // Timer can only be controlled by the assignee (or admins/managers for oversight)
  const canControlTimer = isAssignee;
  const LOCKED_STATUSES = ['done', 'blocked', 'cancelled'];
  const isLocked       = LOCKED_STATUSES.includes(task.status);
  const canDeleteFinal = isCreator || canWrite;

  const chkTotal = chkProgress?.total ?? task.checklist_total ?? 0;
  const chkDone  = chkProgress?.done  ?? task.checklist_done  ?? 0;
  const chkPct   = chkTotal === 0 ? null : Math.round((chkDone / chkTotal) * 100);

  // Stats strip helpers
  const loggedMins   = Math.round((parseFloat(task.total_hours) || 0) * 60);
  const estimatedMins = task.estimated_minutes ? task.estimated_minutes : null;
  const isOverBudget = estimatedMins !== null && loggedMins > estimatedMins;
  const timeNum    = estimatedMins !== null
    ? `${fmtMinutes(loggedMins)} / ${fmtMinutes(estimatedMins)}`
    : fmtMinutes(loggedMins);

  const isRunning = !!task.timer_started_at;

  return (
    <div className="task-detail">

      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <div className="tdv2-topbar">
        <button className="btn-back" onClick={() => navigate(-1)}>←</button>
        <div className="tdv2-topbar-actions">
          {canEdit && !isLocked && (
            <button className="tdv2-header-btn" onClick={() => setEditOpen(true)} title="Edit Title / Description">
              ✎ Edit
            </button>
          )}
          {canDeleteFinal && !isLocked && (
            <button className="tdv2-header-btn danger" onClick={() => setDeleteOpen(true)} title="Delete task">
              🗑 Delete
            </button>
          )}
        </div>
      </div>

      {/* ── Title row: name left · task# + status + priority right ──────── */}
      <div className="tdv2-title-row">
        <h1 className="tdv2-title">{task.title}</h1>
        <div className="tdv2-title-controls">
          {task.task_number && (
            <span className="task-number-badge" style={{ fontSize: 11 }}>{task.task_number}</span>
          )}
          <TaskSourceTag task={task} />
          {/* Inline status selector — always editable by creator/assignee/admin to allow unblocking */}
          <select
            className={`tdv2-inline-sel tdv2-sel-status-${task.status}`}
            value={task.status}
            onChange={e => {
              const newStatus = e.target.value;
              if (['done', 'cancelled'].includes(newStatus) && chkTotal > 0 && chkDone < chkTotal) {
                setShowChkWarning(true);
                return;
              }
              setShowChkWarning(false);
              patch({ status: newStatus });
            }}
            title="Change status"
            disabled={!canEdit}
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          {/* Inline priority selector */}
          <select
            className={`tdv2-inline-sel tdv2-sel-priority-${task.priority}`}
            value={task.priority}
            onChange={e => patch({ priority: e.target.value })}
            title="Change priority"
            disabled={!canEdit || isLocked}
          >
            {PRIORITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {isLocked && canEdit && (
        <div className="banner banner-warning" style={{ marginBottom: 10 }}>
          This task is <strong>{STATUS_OPTIONS.find(o => o.value === task.status)?.label}</strong> — change the status to edit other fields.
        </div>
      )}
      {!canEdit && !isLocked && (
        <div className="banner banner-warning" style={{ marginBottom: 10 }}>
          Only the task creator or assignee can edit this task.
        </div>
      )}
      {error && <div className="banner banner-error" style={{ marginBottom: 10 }}>{error}</div>}
      {showChkWarning && chkTotal > 0 && chkDone < chkTotal && (
        <div className="banner banner-error" style={{ marginBottom: 10 }}>
          {chkTotal - chkDone} item{chkTotal - chkDone !== 1 ? 's' : ''} remaining — task cannot be marked Completed or Cancelled until all items are checked.
        </div>
      )}

      {timerAlert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--bg-modal)', borderRadius: 14, padding: '32px 28px', maxWidth: 420, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.28)', textAlign: 'center', border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <div style={{ fontWeight: 700, fontSize: 17, color: 'var(--text-primary)', marginBottom: 10 }}>Timer Exceeded 2 Hours</div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 24 }}>
              You cannot log more than <strong>2 hrs</strong> at once.<br />
              Click below to log <strong>2 hrs</strong>, stop the timer, and clear it.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={() => setTimerAlert(false)}
                style={{ background: 'var(--btn-secondary-bg)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setTimerAlert(false);
                  setTimerLoading(true);
                  try {
                    const updated = await api.taskTimer(appId, taskId, { action: 'stop', force_minutes: 120 });
                    setTask(updated);
                    clientTimerStart.current = null;
                    await loadTask();
                    setTimeLogReload(n => n + 1);
                  } catch (err) {
                    setError(err.response?.data?.error || err.message);
                  } finally {
                    setTimerLoading(false);
                  }
                }}
                style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
              >
                Log 2 hrs &amp; Stop
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Stats strip — time & checklist at a glance ───────────────── */}
      <div className="tdv2-stats-strip">
        {/* Time: logged / estimated */}
        <span className={`tdv2-stat-pill${isOverBudget ? ' over-budget' : ''}`}>
          <span className="tdv2-stat-icon">⏱</span>
          <span className="tdv2-stat-num">{timeNum}</span>
        </span>
        {/* Checklist: done / total (%) */}
        {chkTotal > 0 && (
          <span className={`tdv2-stat-pill${chkDone === chkTotal ? ' done' : ''}`}>
            <span className="tdv2-stat-icon">✓</span>
            <span className="tdv2-stat-num">{chkDone}/{chkTotal}</span>
            {chkPct !== null && (
              <span className="tdv2-stat-unit">{chkPct}%</span>
            )}
          </span>
        )}
      </div>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <div className="task-detail-tabs">
        {[
          { id: 'overview',  label: 'Overview' },
          { id: 'timeline',  label: 'Timeline' },
          ...(canManage || me?.role === 'admin' || me?.role === 'reporting_manager' ? [{ id: 'quality', label: 'Quality Analysis' }] : []),
        ].map(tab => (
          <button
            key={tab.id}
            className={`task-detail-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════
          TAB: Overview — 2-column layout
          ════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="tdv2-body">

          {/* ── Main column ─────────────────────────────────────────── */}
          <div className="tdv2-main">

            {/* Meta grid — Area · Assigned · Deadline · Est.Time · Logged · Checklist */}
            <div className="tdv2-meta-card">
              {/* Area */}
              <div className="tdv2-field">
                <span className="tdv2-field-label">Area</span>
                <span className="tdv2-field-static">
                  {(() => {
                    if (!task.area_id) return 'Others (no area)';
                    for (const r of roles) {
                      const areas = areasByRole.get(r.id) || [];
                      const a = areas.find(a => a.id === task.area_id);
                      if (a) return a.name;
                    }
                    return `Area #${task.area_id}`;
                  })()}
                </span>
              </div>

              {/* Assigned to */}
              <div className="tdv2-field">
                <span className="tdv2-field-label">Assigned To</span>
                <span className="tdv2-field-static">{memberName(task.assigned_to_user_id)}</span>
              </div>

              {/* Deadline */}
              <div className="tdv2-field">
                <span className="tdv2-field-label">Deadline</span>
                <span className="tdv2-field-static">{task.due_on ? formatDate(task.due_on) : '—'}</span>
              </div>

              {/* Estimated time */}
              <div className="tdv2-field">
                <span className="tdv2-field-label">Est. Time</span>
                <span className="tdv2-field-static">
                  {task.estimated_minutes ? fmtMinutes(task.estimated_minutes) : '—'}
                </span>
              </div>

              {/* Project + Milestone */}
              {task.project_id && (
                <>
                  <div className="tdv2-field">
                    <span className="tdv2-field-label">Project</span>
                    <span className="tdv2-field-static">{task.project_name || `#${task.project_id}`}</span>
                  </div>
                  <div className="tdv2-field">
                    <span className="tdv2-field-label">Milestone</span>
                    <span className="tdv2-field-static">
                      {task.milestone_title || (task.milestone_id ? `#${task.milestone_id}` : '—')}
                    </span>
                  </div>
                </>
              )}

              {/* Agreement */}
              {task.contract_id && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Agreement</span>
                  <span className="tdv2-field-static">
                    {task.agreement_name
                      ? `${task.agreement_name}${task.agreement_number ? ` (${task.agreement_number})` : ''}`
                      : task.agreement_number || `#${task.contract_id}`}
                  </span>
                </div>
              )}

              {/* Service + Service Month — only present on agreement-linked tasks */}
              {task.contract_id && task.agreement_service_id && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Service</span>
                  <span className="tdv2-field-static">
                    {task.service_name || `#${task.agreement_service_id}`}
                  </span>
                </div>
              )}
              {task.contract_id && task.service_month && task.service_year && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Service Month</span>
                  <span className="tdv2-field-static">
                    {SERVICE_MONTHS[task.service_month - 1]} {task.service_year}
                  </span>
                </div>
              )}

              {/* Created by */}
              <div className="tdv2-field">
                <span className="tdv2-field-label">Created By</span>
                <span className="tdv2-field-static">
                  {memberName(task.created_by_user_id)}
                </span>
              </div>
              {task.created_at && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Created At</span>
                  <span className="tdv2-field-static">{formatDateTime(task.created_at)}</span>
                </div>
              )}
              {task.completed_at && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Completed</span>
                  <span className="tdv2-field-static muted" style={{ fontSize: 12 }} title={task.completed_at}>{formatRelative(task.completed_at)}</span>
                </div>
              )}
            </div>

            {/* Timer bar */}
            <div className="tdv2-timer-bar">
              <span className={`timer-display${isRunning ? ' timer-running' : ''}`}
                style={{ fontSize: 20, minWidth: 'auto', letterSpacing: 1 }}>
                {fmtTimer(timerSecs)}
              </span>
              <span className="tdv2-timer-label">
                {isRunning ? '● Running' : 'Timer stopped'}
              </span>
              {isLocked && canEdit ? (
                <button className="btn-timer btn-timer-start" onClick={() => changeStatus('open')} disabled={statusChanging} title="Reopen task">↺ Resume</button>
              ) : !isRunning ? (
                <button className="btn-timer btn-timer-start" onClick={() => timerAction('start')} disabled={timerLoading || isLocked || !canControlTimer} title={!canControlTimer ? 'Only the assignee can start this task' : ''}>▶ Start</button>
              ) : (
                <button className="btn-timer btn-timer-pause" onClick={() => timerAction('pause')} disabled={timerLoading || isLocked || !canControlTimer}>⏸ Pause</button>
              )}
              <button className="btn-timer btn-timer-stop" onClick={() => timerAction('stop')} disabled={timerSecs === 0 || timerLoading || isLocked || !canControlTimer}>
                ⏹ Stop &amp; Log
              </button>
            </div>

            {/* Description */}
            {(task.description || (task.description_attachments?.length > 0)) && (
              <div className="tdv2-section-card">
                <div className="tdv2-section-label">Description</div>
                <DescriptionWithAttachments
                  description={task.description || ''}
                  attachments={task.description_attachments || []}
                />
              </div>
            )}

            {/* Checklist — inline, merged with compliance deliverables & activities */}
            <ChecklistPanel
              key={chkKey}
              appId={appId}
              taskId={task.id}
              onProgressChange={handleProgressChange}
              readOnly={task.status === 'done' || task.status === 'cancelled'}
              canManageItems={false}
              complianceTask={complianceTask}
              complianceSaving={complianceSaving}
              onComplianceToggle={async (field, idx, checked) => {
                if (task.status === 'done' || task.status === 'cancelled') return;
                const current = complianceTask[field] || [];
                const updated = checked
                  ? current.filter(x => x !== String(idx))
                  : [...current, String(idx)];
                const original = complianceTask[field];
                // Optimistic update
                setComplianceTask(t => ({ ...t, [field]: updated }));
                setComplianceSaving(true);
                try {
                  await api.updateContractTask(appId, complianceTask.id, { [field]: updated });
                } catch {
                  setComplianceTask(t => ({ ...t, [field]: original }));
                } finally { setComplianceSaving(false); }
              }}
            />

          </div>

          {/* ── Right sidebar ──────────────────────────────────────── */}
          <div className="tdv2-sidebar" ref={sidebarRef}>
            <TimeLogsPanel
              appId={appId}
              taskId={task.id}
              members={members}
              me={me}
              canManage={canManage || (me?.role === 'admin')}
              onChanged={loadTask}
              reloadTrigger={timeLogReload}
              sidebarMode
            />
            <CommentsPanel
              appId={appId}
              taskId={task.id}
              members={members}
              me={me}
              sidebarMode
            />
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════
          TAB: Timeline
          ════════════════════════════════════════════════════════════════ */}
      {activeTab === 'timeline' && (
        <TimelinePanel appId={appId} taskId={task.id} members={members} />
      )}

      {/* ════════════════════════════════════════════════════════════════
          TAB: Quality Analysis
          ════════════════════════════════════════════════════════════════ */}
      {activeTab === 'quality' && (
        <QualityPanel appId={appId} taskId={task.id} members={members} me={me} taskStatus={task.status} assigneeUserId={task.assigned_to_user_id} />
      )}

      {/* ── Modals ── */}
      {/* key forces a fresh mount each time the modal opens so useState
          initialises from the latest task values, preventing stale form state */}
      <CreateTaskModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        members={members}
        roles={roles}
        areasByRole={areasByRole}
        editTask={editOpen ? task : null}
        onUpdated={async () => { setEditOpen(false); await loadTask(); setChkKey(k => k + 1); }}
        onCreated={async () => {}}
      />

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete this task?"
        message="The task, its comments, time logs, checklist, and timeline will be permanently removed."
        onConfirm={async () => {
          await api.deleteTask(appId, taskId);
          navigate(`/apps/${appId}/work/tasks`);
        }}
        onClose={() => setDeleteOpen(false)}
      />
    </div>
  );
}
